/* eslint-disable complexity */
import { format, sub } from 'date-fns';
import { v4 as uuid } from 'uuid';

import {
  FinancialConfig,
  FinancialDatasetRequest,
  ForecastComparisonDataset,
  ForecastComparisonRequest,
  ForecastListItem,
  ReportingExtraColumn,
  ReportingTemplateFormula,
  RowFormula,
  SingleOrgDatasetRequest,
  SingleOrgForecastComparisonRequest
} from '@futrli-sdk/predict';

import { forceLocalDate } from '../utils/dates';
import { getFieldsByGroupingWithLevel, getFieldWithChildren, widgetHasTwoAxes } from './utils-data';
import { getStartDate } from './utils-dates';
import { actualsFinDataForecastId, DATE_FORMAT } from './constants';
import {
  CARD_TYPES,
  COMPARISON_OPTIONS,
  DataField,
  DatasetComparisonChild,
  GROUPING_OPTIONS,
  LayoutData,
  PeriodType,
  WIDGET_TYPES
} from './types';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';

/**
 * Determines whether a variance/column calculation is compatible with the current dataset configuration.
 * Relies on the `disabledIfMultiDS` and `disabledIfOneDS` flags set on the DataField when it was selected.
 */
export const isVarianceCompatibleWithDatasetCount = (
  variance: DataField,
  isMultiDS: boolean
): boolean => {
  if (isMultiDS) {
    return !variance.disabledIfMultiDS;
  }
  return !variance.disabledIfOneDS;
};

/**
 * Return an array of refs for the given data field and its children
 * @param field - The data field to retrieve the refs for
 * @param widget - The widget the data field belongs to
 * @param allFinFields - The array of all financial data fields available to the report
 * @param addAccountsGroupingIfAssumptions - If true, will add the 'accounts' grouping if assumptions
 */
export const getDataFieldRefs = (
  field: DataField,
  widget: LayoutData,
  allFinFields: DataField[],
  addAccountsGroupingIfAssumptions: boolean
) => {
  const fwc = getFieldWithChildren(allFinFields, field.ref, field.isSecondary);
  if (fwc) {
    const { grouping, excludedChildren, includedChildren } = getWidgetFieldOptions(widget, field);

    if (addAccountsGroupingIfAssumptions) {
      if (grouping.includes(GROUPING_OPTIONS.ASSUMPTIONS) && !grouping.includes(GROUPING_OPTIONS.ACCOUNTS)) {
        // if assumptions are included, we want to include accounts as well
        grouping.push(GROUPING_OPTIONS.ACCOUNTS);
      }
    }

    const groupingParam = isSingleGroupingWidget(widget) && grouping.length > 1 ? [grouping[0]] : grouping;
    const fields = getFieldsByGroupingWithLevel(fwc, groupingParam, false, [], excludedChildren, includedChildren);

    return fields.map(f => f.ref);
  }
};

/**
 * Returns an object full of ForecastComparisonDatasets generated from the datasets in the given widget
 * @param widget - The widget to get the datasets from
 * @param allForecasts - The array of forecasts available to the report (use the dummy template forecasts if isTemplate=true)
 * @param financialConfig - The financial config for the organisation
 */
export const getForecastComparisonDatasets = (
  widget: LayoutData,
  allForecasts: (ForecastListItem | ConsolidatedForecastListItem)[],
  financialConfig: FinancialConfig
) => {
  const widgetOptions = widget[CARD_TYPES.WIDGET]!;
  const { startDate, startDate1YearPrevious } = getWidgetOptions(widget, financialConfig);

  const datasets: Record<string, ForecastComparisonDataset> = {};
  const dsIdtoUUIDMap: Record<string, string> = {};
  const dsUUIDToIdMap: Record<string, string> = {};

  widgetOptions.selectedDatasets!.forEach(ds => {
    const f = allForecasts.find(f => f.id === ds);
    if (f) {
      const dsUUID = uuid();

      dsIdtoUUIDMap[ds] = dsUUID;
      dsUUIDToIdMap[dsUUID] = ds;

      const isActualsOnly = isActualsOnlyDataset(f.id);

      datasets[dsUUID] = {
        name: f.name.toLowerCase() === 'daily tracker forecast' ? 'Tracker' : f.name,
        forecast_id: isActualsOnly ? actualsFinDataForecastId : f.id,
        consolidated_forecast_id: isActualsOnly ? actualsFinDataForecastId : f.id,
        start_date: f.id === 'prior-year-actuals' ? startDate1YearPrevious : startDate,
        actuals_only: isActualsOnly
      } as ForecastComparisonDataset;
    }
  });

  return { datasets, dsIdtoUUIDMap, dsUUIDToIdMap };
};

/**
 * Returns an array of ForecastComparisonDetails for given widget / forecast / datasource data
 * @param widget - The widget to use the selected comparions from
 * @param forecasts - All forecasts available to the report
 * @param dsIdtoUUIDMap - Datasource Id to UUID map
 * @param dsUUIDToIdMap - Datasource UUID to ID map
 */
export const getForecastComparisons = (
  widget: LayoutData,
  forecasts: (ForecastListItem | ConsolidatedForecastListItem)[],
  dsIdtoUUIDMap: Record<string, string>,
  dsUUIDToIdMap: Record<string, string>
) => {
  const widgetOptions = widget[CARD_TYPES.WIDGET]!;
  const comparisons: { primary_dataset_id: string; comparative_dataset_id: string }[] = [];
  let remainingDatasets = widgetOptions.selectedDatasets!;

  (widgetOptions.selectedComparisons || []).forEach(comp => {
    comp.comparisons.forEach(ds => {
      const forecast1 = forecasts.find(f => f.id === ds[0]);
      const forecast2 = forecasts.find(f => f.id === ds[1]);

      if (!forecast1 || !forecast2) {
        return;
      }

      const comparisonNeeded = !comparisons.find(
        c =>
          (c.primary_dataset_id === dsIdtoUUIDMap[ds[0]] && c.comparative_dataset_id === dsIdtoUUIDMap[ds[1]]) ||
          (c.primary_dataset_id === dsIdtoUUIDMap[ds[1]] && c.comparative_dataset_id === dsIdtoUUIDMap[ds[0]])
      );

      if (comparisonNeeded) {
        comparisons.push({
          primary_dataset_id: dsIdtoUUIDMap[ds[0]],
          comparative_dataset_id: dsIdtoUUIDMap[ds[1]] || ''
        });

        // removing any dataset that is used in a comparison from the array of remainingDatasets
        remainingDatasets = remainingDatasets.filter(d => d !== ds[0] && d !== ds[1]);
      }
    });
  });

  // Add remaining datasets that aren't included in any comparison so that we can have data for them as well.
  if (remainingDatasets.length) {
    remainingDatasets.forEach(ds =>
      comparisons.push({ primary_dataset_id: dsIdtoUUIDMap[ds], comparative_dataset_id: '' })
    );
  }

  // Sort and return comparisons array
  return comparisons.sort(
    (c1, c2) =>
      (widgetOptions.selectedDatasets?.indexOf(dsUUIDToIdMap[c1.primary_dataset_id]) || 0) -
      (widgetOptions.selectedDatasets?.indexOf(dsUUIDToIdMap[c2.primary_dataset_id]) || 0)
  );
};

/**
 * Returns the selected variances for the given widget
 * @param widget - The widget to extract the variances from
 */
export const getForecastComparisonVariances = (widget: LayoutData) => {
  const widgetOptions = widget[CARD_TYPES.WIDGET]!;
  const variances: { ref: string }[] = [];

  (widgetOptions.selectedComparisons || []).forEach(comp => {
    if (!variances.find(sv => sv.ref === comp.ref)) {
      variances.push({ ref: comp.ref });
    }
  });

  // Comparison queries are always multi-DS, so filter accordingly
  (widgetOptions.selectedVariances || []).forEach(v => {
    if (!isVarianceCompatibleWithDatasetCount(v, true)) {
      return;
    }
    if (!variances.find(sv => sv.ref === v.ref)) {
      variances.push({ ref: v.ref });
    }
  });

  return variances;
};

/**
 * Returns the ref of the given formula field if it exists in the allFormulas array
 * @param field - The field to extract the ref from
 * @param allFormulas - The array of all formulas available to the report
 */
export const getFormulaFieldRef = (field: DataField, allFormulas: (RowFormula | ReportingTemplateFormula)[]) => {
  const isFormulaStillAvailable = !!allFormulas.find(formula => formula.ref === field.ref);
  if (isFormulaStillAvailable) {
    return field.ref;
  }
};

/**
 * A convienience utility to extract various bits of options data for a selected financial
 * data field belonging to a widget
 * @param widget - The widget the data field belongs to
 * @param field - The selected financial data field
 */
export const getWidgetFieldOptions = (widget: LayoutData, field: DataField) => {
  const widgetOptions = widget[CARD_TYPES.WIDGET];
  const fieldOptions = (widgetOptions?.finFieldsOptions || []).find(e => e.ref === field.ref);

  return {
    fieldOptions,
    grouping: [...(fieldOptions?.options?.grouping || [])],
    excludedChildren: fieldOptions?.options?.excludedChildren,
    includedChildren: fieldOptions?.options?.includedChildren
  };
};

/**
 * A convienience utility to extract various bits of options data for a widget
 * @param widget - The widget to extract the options from
 * @param financialConfig - The financial config for the current organisation
 */
export const getWidgetOptions = (widget: LayoutData, financialConfig: FinancialConfig) => {
  const widgetOptions = widget[CARD_TYPES.WIDGET];
  const calendarOptions = widgetOptions?.calendarOptions;

  if (calendarOptions) {
    const isPreviousComparison = calendarOptions.comparision === COMPARISON_OPTIONS.PREVIOUS;
    const startDate = getStartDate(calendarOptions.startDate, calendarOptions.type as PeriodType, financialConfig);

    if (startDate === 'Invalid Date') {
      return {};
    }

    return {
      periodCount: isPreviousComparison ? -calendarOptions.periods - 1 : calendarOptions.periods + 1,
      startDate,
      startDate1YearPrevious: format(sub(forceLocalDate(startDate), { years: 1 }), DATE_FORMAT)
    };
  }

  return {};
};

/**
 * Returns an array of refs for all of the selected financial fields that the widget will
 * require data for
 * @param widget - The widget to generate the list of refs from
 * @param allFinFields - A list of all of the financial fields available to the report
 * @param allFormulas - A list of all of the formulas available to the report
 */
export const getWidgetQueryFieldRefs = (
  widget: LayoutData,
  allFinFields: DataField[],
  allFormulas: (RowFormula | ReportingTemplateFormula)[],
  addAccountsGroupingIfAssumptions: boolean
) => {
  const widgetOptions = widget[CARD_TYPES.WIDGET];

  const queryFieldRefs = widgetOptions?.selectedFinFields.map(field => {
    if (isFormulaField(field)) {
      return getFormulaFieldRef(field, allFormulas) || '';
    }

    if (isActiveDataField(field, widget)) {
      return getDataFieldRefs(field, widget, allFinFields, addAccountsGroupingIfAssumptions) || '';
    }

    return '';
  });

  // Filter out empty strings and then flatten array
  return (queryFieldRefs || []).filter(s => !!s).flat();
};

/**
 * Returns the selected variances for the given widget
 * @param widget - The widget to extract the selected variances from
 * @param datasetComparisonChildren - The array of all possible dataset comparison options?
 * @param isLegacy - If this widget is a legacy widget (pre-multi data set)
 */
export const getWidgetSelectedVariances = (
  widget: LayoutData,
  datasetComparisonChildren: DatasetComparisonChild[],
  isLegacy: boolean
) => {
  const widgetOptions = widget[CARD_TYPES.WIDGET];
  const isMultiDS = (widgetOptions?.selectedDatasets?.length || 0) > 1;

  if (widgetOptions?.widgetType === WIDGET_TYPES.TABLE) {
    return (widgetOptions.selectedVariances || [])
      .filter(v => {
        if (!isVarianceCompatibleWithDatasetCount(v, isMultiDS)) {
          return false;
        }
        const comp = datasetComparisonChildren.find(e => e.ref === v.ref);
        return !comp || (!isLegacy && comp.isGeneric);
      })
      .map(v => ({ ref: v.ref }));
  }

  return [];
};

/**
 * Returns true if the given dataset id is for an actuals only dataset
 * @param id - ID of the dataset
 */
export const isActualsOnlyDataset = (id: string) => {
  return id === 'actual-data' || id === 'prior-year-actuals';
};

/**
 * Returns true or false depending on if the given field is active on the given widget
 * @param field - The fiueld to check
 * @param widget - The widget the field is used on
 */
export const isActiveDataField = (field: DataField, widget: LayoutData) => {
  const widgetOptions = widget[CARD_TYPES.WIDGET]!;
  return !field.isSecondary || widgetHasTwoAxes(widgetOptions.widgetType as WIDGET_TYPES);
};

/**
 * Returns true or false depending on if the given widget data field is a formula or not
 * @param field - The data field to check
 */
export const isFormulaField = (field: DataField) => {
  return field.classifier === 'row-formula' || field.classifier === 'template-formula';
};

/**
 * Returns true or false depending on if the given widget is one that only supports a single grouping
 * @param widget - The widget to check
 */
export const isSingleGroupingWidget = (widget?: LayoutData) => {
  const widgetType = widget?.[CARD_TYPES.WIDGET]?.widgetType;
  return widgetType === WIDGET_TYPES.PIE || widgetType === WIDGET_TYPES.DONUT || widgetType === WIDGET_TYPES.DATA_CARD;
};

/**
 * Returns true if any of the selected financial fields selected for the given widget
 * includes the 'assumptions' grouping option
 * @param widget - The widget to check against
 */
export const isWidgetFieldUsingAssumptionsGrouping = (widget: LayoutData) => {
  const widgetOptions = widget[CARD_TYPES.WIDGET];
  const widgetType = widgetOptions?.widgetType as WIDGET_TYPES;

  if (!widgetOptions) {
    return false;
  }

  return !!widgetOptions.selectedFinFields.find(field => {
    if (!field.isSecondary || widgetHasTwoAxes(widgetType)) {
      const { grouping } = getWidgetFieldOptions(widget, field);
      return grouping.includes(GROUPING_OPTIONS.ASSUMPTIONS);
    }
  });
};

/**
 * Returns true or false depending upon if the query has all of the user input required to
 * query for comparison data
 * @param widget - The widget data to check
 */
export const isWidgetReadyForComparisonQuerying = (widget?: LayoutData) => {
  const widgetOptions = widget?.[CARD_TYPES.WIDGET];
  return !!(
    widgetOptions &&
    widget.type === CARD_TYPES.WIDGET &&
    widgetOptions.widgetType === WIDGET_TYPES.TABLE &&
    widgetOptions.calendarOptions?.type &&
    widgetOptions.selectedFinFields.length &&
    widgetOptions.selectedComparisons?.length &&
    widgetOptions.selectedDatasets?.length &&
    widgetOptions.selectedDatasets?.length > 1 &&
    typeof widgetOptions.calendarOptions?.periods === 'number' &&
    widgetOptions.calendarOptions?.startDate
  );
};

/**
 * Returns true or false depending upon if the query has all of the user input required to
 * query for data
 * @param widget - The widget data to check
 */
export const isWidgetReadyForQuerying = (widget?: LayoutData) => {
  const widgetOptions = widget?.[CARD_TYPES.WIDGET];
  return !!(
    widgetOptions &&
    widget.type === CARD_TYPES.WIDGET &&
    widgetOptions.selectedFinFields.length &&
    widgetOptions.calendarOptions?.type &&
    typeof widgetOptions.calendarOptions?.periods === 'number' &&
    widgetOptions.calendarOptions?.startDate
  );
};

const DATASET_COMPARISON_REF_PREFIX = 'v1:accrual:dataset_comparison:';

/**
 * Checks if a variance ref is a dataset comparison ref (e.g. variance_value, variance_percent)
 * as opposed to a generic column calculation (e.g. total, average).
 */
export const isDatasetComparisonRef = (ref: string) => ref.startsWith(DATASET_COMPARISON_REF_PREFIX);

export type V2FinancialDatasetRequest = Omit<SingleOrgDatasetRequest, 'selected_variances'> & {
  consolidated_forecast_id?: string;
};

/**
 * Transforms generated financial-data query datasets for V2 endpoints.
 * Renames `selected_variances` to `extra_columns`.
 */
export const transformFinancialDataQueriesForV2 = (
  queries: Record<string, FinancialDatasetRequest>
): Record<string, V2FinancialDatasetRequest> => {
  const transformed = {} as Record<string, V2FinancialDatasetRequest>;

  Object.entries(queries).forEach(([key, query]) => {
    const { selected_variances, ...rest } = query;
    transformed[key] = {
      ...rest,
      extra_columns: (selected_variances || []) as unknown as ReportingExtraColumn[]
    } as unknown as V2FinancialDatasetRequest;
  });

  return transformed;
};

/**
 * Transforms multi-forecast-comparison queries for V2 endpoints.
 * - Replaces `comparisons` + `selected_variances` with `extra_columns` and `dataset_order`
 * - Comparison refs (v1:accrual:dataset_comparison:*) get primary/comparative dataset IDs
 * - Generic refs (e.g. total, average) remain as simple {ref} entries
 * - `dataset_order` lists all dataset IDs from the datasets section
 */
export const transformComparisonQueriesForV2 = (
  queries: Record<string, ForecastComparisonRequest>
): Record<string, SingleOrgForecastComparisonRequest> => {
  const transformed = {} as Record<string, SingleOrgForecastComparisonRequest>;

  Object.entries(queries).forEach(([key, query]) => {
    const { comparisons, selected_variances, ...rest } = query;

    // Build dataset_order from the datasets keys
    const dataset_order = Object.keys(query.datasets);

    // Build extra_columns from selected_variances
    const extra_columns: ReportingExtraColumn[] = [];

    (selected_variances || []).forEach(variance => {
      if (isDatasetComparisonRef(variance.ref)) {
        // For comparison refs, attach primary/comparative dataset IDs from each comparison pair
        (comparisons || []).forEach(comp => {
          if (comp.comparative_dataset_id && comp.comparative_dataset_id !== '') {
            extra_columns.push({
              ref: variance.ref,
              primary_dataset_id: comp.primary_dataset_id,
              comparative_dataset_id: comp.comparative_dataset_id
            });
          }
        });
      } else {
        // Generic column calculations (total, average, etc.) — no dataset association
        extra_columns.push({ ref: variance.ref } as ReportingExtraColumn);
      }
    });

    transformed[key] = {
      ...rest,
      dataset_order,
      extra_columns
    } as unknown as SingleOrgForecastComparisonRequest;
  });

  return transformed;
};
